// Helpers for Crystallize tiles: reading tile relations from a document, and condition-based
// visibility filtering. Isomorphic (server + client) and pure, so both stay deterministic to test.
import { componentByTypename } from '@/ui/utils/componentByTypename';
import type { CrystallizeDocument } from './crystallize-document-helper';

type DocumentField = NonNullable<CrystallizeDocument['components']>[number];

type ItemRelationsField = Extract<
NonNullable<DocumentField['content']>,
{ __typename: 'ItemRelationsContent' }
>;

/**
 * A single tile (related document) linked from an item-relations field. The generic GetDocumentData
 * query does not select `__typename` on relation items, so we stamp it on — the tile renderer
 * (`TilesArrayRenderer`) requires `__typename: 'Document'` to accept a tile.
 */
export type CrystallizeTile = NonNullable<ItemRelationsField['items']>[number] & {
  __typename: 'Document';
};

/**
 * Returns the tiles (related documents) linked from the document's `componentId` item-relations
 * field, or an empty array when that field is absent or is not an item-relations component.
 */
export function getTilesFromField(
  document: CrystallizeDocument,
  componentId: string,
): CrystallizeTile[] {
  const field = document.components?.find((component) => component.id === componentId);
  const relations = componentByTypename(field?.content, 'ItemRelationsContent');

  // GetDocumentData relation items don't carry a usable `__typename` at runtime, but the tile
  // renderer requires `__typename: 'Document'`. Tiles are always documents, so we normalise it here.
  return (relations?.items ?? []).map((item) => ({ ...item, __typename: 'Document' as const }));
}

// --- Condition-based tile visibility ---
// Tiles can be tagged with "/hide-conditions/hide-is-<condition>" topics. A tile is HIDDEN when ANY
// of its hide-topics maps to a condition that is currently true; a tile without hide-topics is always
// visible (default-visible). Mirrors tile-country-visibility.ts.

export const HIDE_TOPIC_PREFIX = '/hide-conditions/';

/**
 * Order-derived conditions; a tile tagged with the matching hide-topic is hidden when true. Payment is
 * a two-state success/failure split (as the page has always been): a pending/open payment counts as
 * success, so `isPaymentSuccess` and `isPaymentFailure` are complementary.
 */
export interface TileVisibilityConditions {
  isPaymentFailure: boolean;
  isPaymentSuccess: boolean;
  isVirtualOrder: boolean;
  isNukiClubMember: boolean;
  isLoggedIn: boolean;
}

// Topic segment (after the prefix) -> the condition key it hides on.
const TOPIC_TO_VISIBILITY_CONDITION: Record<string, keyof TileVisibilityConditions> = {
  'hide-is-payment-failure': 'isPaymentFailure',
  'hide-is-payment-success': 'isPaymentSuccess',
  'hide-is-virtual-order': 'isVirtualOrder',
  'hide-is-nuki-club-member': 'isNukiClubMember',
  'hide-is-logged-in': 'isLoggedIn',
};

interface TopicReference {
  path: string;
}

export interface ConditionFilterableTile {
  __typename?: string;
  topics?: TopicReference[] | null;
}

export function isTileVisible(
  tile: ConditionFilterableTile,
  conditions: TileVisibilityConditions,
): boolean {
  const hideTopicSegments = (tile.topics ?? [])
    .map((topic) => topic.path.toLowerCase())
    .filter((path) => path.startsWith(HIDE_TOPIC_PREFIX))
    .map((path) => path.slice(HIDE_TOPIC_PREFIX.length).split('/')[0]);

  // No hide-topics -> visible. Otherwise hidden as soon as one hide-topic's condition is true.
  return !hideTopicSegments.some((segment) => {
    const conditionKey = TOPIC_TO_VISIBILITY_CONDITION[segment];

    return conditionKey !== undefined && conditions[conditionKey];
  });
}

export function filterVisibleTiles<T extends ConditionFilterableTile>(
  tiles: T[],
  conditions?: TileVisibilityConditions,
): T[] {
  // No conditions resolved for this surface -> nothing to hide.
  if (!conditions) {
    return tiles;
  }

  return tiles.filter((tile) => isTileVisible(tile, conditions));
}
